import * as React from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Base64 } from 'js-base64';
import { FormattedMessage, useIntl } from 'react-intl';
import { ElsPaginator, ElsAccordionGroup, ElsAccordionItem } from '@scopus/experimental-components-wrappers-react';
import Error from '../../components/Error/Error';
import Facets, { createFacets } from '../../components/Facets/Facets';
import { GET_VIEW } from '../../graphql/queries';
import { createGraphQlRequest } from '../../utils/createGraphQlRequest';
import { ViewMetadata, Selection, Entity } from '../../graphql/generated/graphql';
import { Filter, FragmentConfig, MeasureRequest, ActionName } from '../types';
import { useEventSystem } from '../../components/EventProvider/EventProvider';
import TableComponent from './TableComponent';
import { AcknowledgedValuesProvider } from '../../context/TableCellContext';
import ScopusHOC from '../../components/ScopusHOC/ScopusHOC';

import styles from './table.module.scss';

export interface OptionType {
  label: string;
  value: any;
  selected?: boolean;
}

export interface ScopusSelect extends HTMLElement {
  onChange: (newSortOption: any) => void;
}

function generateCSVFile({ headers, entities }: { headers: string[]; entities: Entity[] }): string {
  const csvHeaders = headers.map((item: string) => JSON.stringify(item)).join(',');
  const csvValues = entities.reduce((output: string, entity: Entity) => {
    const entityData = JSON.parse(entity.dataJson).map((entityDataItem: any[]) =>
      entityDataItem.map((item: any) => JSON.stringify(item?.toString() ?? 'N/A')),
    );
    return `${output ? `${output}\n` : ''}${entityData.join('\n')}`;
  }, '');

  return `${csvHeaders}\n${csvValues}`;
}

function getTableHeaders(config: FragmentConfig, metadata: ViewMetadata[]): string[] {
  return config.visualisations[0].measures.map((measure, index) => {
    return measure?.label ?? (metadata ? (metadata[index]?.label as string) : measure.measureName);
  });
}

const Table = React.memo(({ config }: { config: FragmentConfig }) => {
  const [pageNumber, setPageNumber] = React.useState(1);
  const [pageSize, setPagePageSize] = React.useState(config.visualisations[0].defaultPageSize || 1);
  const rowCountRef = React.useRef<number | null>(null);
  const { dispatchEvent } = useEventSystem();
  const intl = useIntl();

  const lowestPostfix = intl.formatMessage({
    id: 'table.sortBy.lowestPostfix',
    defaultMessage: 'lowest',
    description: 'Table visualization. Postfix lowest of the sort dropdown',
  });

  const highestPostfix = intl.formatMessage({
    id: 'table.sortBy.highestPostfix',
    defaultMessage: 'highest',
    description: 'Table visualization. Postfix highest of the sort dropdown',
  });

  const oldestPostfix = intl.formatMessage({
    id: 'table.sortBy.oldestPostfix',
    defaultMessage: 'oldest',
    description: 'Table visualization. Postfix oldest of the sort dropdown',
  });

  const newestPostfix = intl.formatMessage({
    id: 'table.sortBy.newestPostfix',
    defaultMessage: 'newest',
    description: 'Table visualization. Postfix newest of the sort dropdown',
  });

  function getSortLabelPostfix(metadata: ViewMetadata, ascending = false): string {
    let postfix;

    switch (metadata.type) {
      case 'CURRENCY':
      case 'NUMBER':
        postfix = ascending ? lowestPostfix : highestPostfix;
        break;
      case 'YEAR':
      case 'DATE':
      case 'TIMESTAMP':
        postfix = ascending ? oldestPostfix : newestPostfix;
        break;
      case 'STRING':
      default:
        postfix = ascending ? 'A-Z' : 'Z-A';
        break;
    }
    return ` (${postfix})`;
  }

  function isSelectedSortByOption(measure: MeasureRequest, config: FragmentConfig): boolean {
    return (
      ((measure.ascending && config.entities[0].selection === Selection.Bottom) ||
        (!measure.ascending && config.entities[0].selection === Selection.Top)) &&
      config.entities[0].sortBy?.measureName === measure.measureName
    );
  }

  function getSortByOptions(config: FragmentConfig, headers: string[], metadata: ViewMetadata[]): OptionType[] {
    const mapMeasureToOption = (measure: MeasureRequest, index: number): OptionType => ({
      value: measure,
      label: headers[index] + getSortLabelPostfix(metadata[index], measure.ascending),
      selected: isSelectedSortByOption(measure, config),
    });
    const options: OptionType[] = [];
    config.visualisations[0].measures.forEach((measure, index) => {
      if (!!measure.sortable && metadata) {
        options.push(mapMeasureToOption({ ascending: false, ...measure }, index));
        options.push(mapMeasureToOption({ ascending: true, ...measure }, index));
      }
    });
    return options;
  }

  /**
   * This ref is used to control if we should trigger
   * the download operation.
   * In the cases where the user has already downloaded,
   * if we don't track it, on every state change (e.g. change sort mode),
   * it would download a new file =)
   **/
  const shouldDownload = React.useRef(false);

  const onPaginated = React.useCallback(({ detail }: CustomEvent) => {
    setPageNumber(detail.page + 1);
  }, []);

  const onPageSizeChanged = React.useCallback(({ detail }: CustomEvent) => {
    setPagePageSize(detail.newPageSize);
  }, []);

  const [sortByMeasure, setSortByMeasure] = React.useState(config.entities[0].sortBy);
  /* On initialState it's useless, but on sortChange must be re-applied */
  config.entities[0].sortBy = sortByMeasure;
  if (sortByMeasure?.ascending != null) {
    config.entities[0].selection = sortByMeasure?.ascending ? Selection.Bottom : Selection.Top;
  }

  //  Facet states
  const [filters, setFilters] = React.useState<Filter>(config.filters || {});
  const [selectedFacetId, setSelectedFacetId] = React.useState<string>('all');

  const request = createGraphQlRequest({ config, pageNumber, pageSize, filters });
  const { loading, data, error, refetch } = useQuery(GET_VIEW, {
    variables: {
      request,
    },
    fetchPolicy: 'no-cache',
  });

  const [getDataForDownload, { loading: downloadDataLoading, data: downloadData }] = useLazyQuery(GET_VIEW, {
    fetchPolicy: 'no-cache',
  });

  const metadata = data?.view?.viewMetadata;
  const headers = getTableHeaders(config, metadata);
  const sortByOptions = getSortByOptions(config, headers, metadata);
  const sortByRef = React.useRef<ScopusSelect | null>(null);
  const entitiesRows = data?.view?.entitiesResponse ?? [];
  // todo: to be  moved inside the table component using a Table component prop rows ehile moving the other table capabilities(export, pagination, sort).https://elsevier.atlassian.net/browse/FC-2648
  const totalRows = data?.view?.entitiesResponse[0].totalRowCount ?? 0;
  const handleExportClick = React.useCallback(() => {
    if (totalRows > 0) {
      getDataForDownload({
        variables: {
          request: createGraphQlRequest({ config, pageSize: totalRows }),
        },
      });

      // Enables the download on re-render
      shouldDownload.current = true;
    }
  }, [getDataForDownload, config, totalRows]);

  React.useEffect(() => {
    if (!downloadDataLoading && downloadData && shouldDownload.current) {
      const csvInBase64 = Base64.encode(
        generateCSVFile({ headers, entities: downloadData.view.entitiesResponse[0].entities }),
      );

      /** Generates an anchor to trigger the 'download' approach */
      const a = document.createElement('a');
      a.download = 'export.csv';
      a.href = `data:text/csv;base64,${csvInBase64}`;
      a.click();

      // Prevents downloads on re-render
      shouldDownload.current = false;
    }
  }, [downloadDataLoading, downloadData, headers]);

  React.useEffect(() => {
    if (!(error || loading) && totalRows !== rowCountRef.current) {
      rowCountRef.current = totalRows;
      dispatchEvent('totalRowCount', { totalRowCount: totalRows });
    }
  }, [dispatchEvent, error, loading, totalRows]);

  React.useLayoutEffect(() => {
    if (sortByRef.current) {
      sortByRef.current.onChange = (newSortOption: any) => {
        const sortByMeasure = newSortOption.value;
        setSortByMeasure(sortByMeasure);
      };
    }
  }, [sortByRef, totalRows]);

  if (error) {
    return (
      <div className={styles.grants}>
        <Error error={error} refresh={refetch} />
      </div>
    );
  }

  const showExportButton = config.visualisations[0].download ?? true;
  const sortByLabel = intl.formatMessage({
    id: 'table.sortBy.label',
    defaultMessage: 'Sort by',
    description: 'Table visualization. Label of the Sort by dropdown.',
  });
  const facetsFromConfig = config?.visualisations[0].facets;
  const facets = createFacets(config.filters, facetsFromConfig);
  function getIdsFromMeasureActions(measures: MeasureRequest[], actionName: ActionName): string[] {
    return (
      measures
        ?.find((measure) => measure?.actions?.some((action) => action.actionName === actionName))
        ?.actions?.find((action) => action.actionName === actionName)?.ids ?? []
    );
  }
  const acknowledgedValuesFromConfig = {
    [ActionName.removeGrant]: getIdsFromMeasureActions(config?.visualisations[0].measures, ActionName.removeGrant),
    [ActionName.addGrant]: getIdsFromMeasureActions(config?.visualisations[0].measures, ActionName.addGrant),
  };
  return (
    <ScopusHOC>
      <AcknowledgedValuesProvider valuesFromConfig={acknowledgedValuesFromConfig}>
        <div className={`micro-ui-root ${styles.grants}`}>
          {config.title && config.visualisations[0].showTitle && entitiesRows?.[0]?.entities.length > 0 ? (
            <els-typography tag="h3" variant="quaternary-header">
              {config.title}
            </els-typography>
          ) : null}
          {facets.map((facet) => (
            <Facets
              key={facet.type}
              mainEntity={config.entities[0].entity}
              facet={facet}
              filters={config.filters}
              selectedId={selectedFacetId}
              onClick={(id) => {
                dispatchEvent('clickFacet', { id });
                setPageNumber(1);
                setSelectedFacetId(id as string);
                setFilters({
                  [facet.type]: id === 'all' ? facet.ids : [id.toString()],
                });
              }}
            />
          ))}

          <>
            <div className="micro-ui-namespace">
              <els-toolbar class={styles.toolbar}>
                <ul data-align="left">
                  {showExportButton && (
                    <li>
                      {totalRows > 0 ? (
                        <button
                          type="button"
                          className="button--link"
                          onClick={handleExportClick}
                          disabled={downloadDataLoading}
                        >
                          <span className="button__text">
                            <FormattedMessage
                              id="table.exportAll.label"
                              defaultMessage="Export all"
                              description="Table visualization. Label of the Export all button"
                            />
                          </span>
                        </button>
                      ) : (
                        loading && <els-skeleton width="100px" />
                      )}
                    </li>
                  )}
                </ul>
                {sortByOptions.length > 0 && (
                  <ul data-align="right">
                    <li>
                      {totalRows > 0 ? (
                        <els-select
                          label={sortByLabel}
                          small="true"
                          options={JSON.stringify(sortByOptions)}
                          ref={sortByRef}
                        />
                      ) : (
                        loading && <els-skeleton width="200px" />
                      )}
                    </li>
                  </ul>
                )}
              </els-toolbar>
            </div>
            {loading || entitiesRows.length === 1 ? (
              <TableComponent
                config={config}
                headers={headers}
                loading={loading}
                metadata={data?.view?.viewMetadata}
                rows={entitiesRows?.[0]?.entities ?? []}
                entityRequest={request}
                rowStartCount={(pageNumber - 1) * pageSize}
              />
            ) : (
              <ElsAccordionGroup multiple>
                {entitiesRows.map((entityRow: any, i: number) => {
                  const prunedEntityRequest = {
                    ...request,
                    entities: [request.entities[i]],
                  };
                  return (
                    <ElsAccordionItem
                      open={i === 0}
                      controlLabel={entityRow.name || `Query ${i + 1}`}
                      title={entityRow.name || `Query ${i + 1}`}
                      key={entityRow.name || i}
                    >
                      <div slot="content">
                        <TableComponent
                          config={config}
                          headers={headers}
                          rows={entityRow.entities}
                          entityRequest={prunedEntityRequest}
                          loading={loading}
                          metadata={data?.view?.viewMetadata}
                          rowStartCount={(pageNumber - 1) * pageSize}
                        />
                      </div>
                    </ElsAccordionItem>
                  );
                })}
              </ElsAccordionGroup>
            )}
            {totalRows > 0 && !!config.visualisations[0].defaultPageSize && (
              <div className={styles.pagination}>
                <div className="micro-ui-namespace">
                  <ElsPaginator
                    activePage={pageNumber - 1}
                    labelDisplay={intl.formatMessage({
                      id: 'table.pagination.pageSizeLabel',
                      defaultMessage: 'Display',
                      description: 'Table visualization. Label for the page size dropdown.',
                    })}
                    labelNext={intl.formatMessage({
                      id: 'table.pagination.nextButtonLabel',
                      defaultMessage: 'Next',
                      description: 'Table visualization. Label of the Next button on the pagination',
                    })}
                    labelPrevious={intl.formatMessage({
                      id: 'table.pagination.previousButtonLabel',
                      defaultMessage: 'Previous',
                      description: 'Table visualization. Label of the Previous button on the pagination',
                    })}
                    labelResults={intl.formatMessage({
                      id: 'table.pagination.pageSizeResultsSuffix',
                      defaultMessage: 'results',
                      description:
                        'Table visualization. Suffix for each option on the page size dropdown. E.g. 10 results',
                    })}
                    onPageSizeChanged={onPageSizeChanged}
                    onPaginated={onPaginated}
                    pageSize={pageSize}
                    pageSizeControl={config.visualisations[0].showPageSizeControl ?? true}
                    totalAmount={totalRows}
                  />
                </div>
              </div>
            )}
          </>
        </div>
      </AcknowledgedValuesProvider>
    </ScopusHOC>
  );
});

Table.displayName = 'Table';
export default Table;
